import { Temporal } from 'temporal-polyfill';
import { DayOfWeek, DayOfWeekLabelMap } from '../data/meeting-block.js';
import { isSameDay } from './index.js';

export function formatPlainTime(
  time: Temporal.PlainTime,
  options?: Intl.DateTimeFormatOptions,
  locale = 'en-US',
): string {
  return time.toLocaleString(locale, { timeStyle: 'short', ...options }).toLowerCase();
}

export function formatPlainDate(
  date: Temporal.PlainDate,
  options?: { separator?: string; includeYear?: boolean },
): string {
  const { separator = '/', includeYear = true } = options || {};
  const { isoMonth, isoDay, isoYear } = date.getISOFields();
  const month = isoMonth.toString().padStart(2, '0');
  const day = isoDay.toString().padStart(2, '0');
  const year = isoYear.toString().padStart(2, '0');
  return `${month}${separator}${day}${includeYear ? `${separator}${year}` : ''}`;
}

export function formatDuration(
  duration: Temporal.Duration,
  options?: Intl.DateTimeFormatOptions,
  locale = 'en-GB',
): string {
  return duration.toLocaleString(locale, options);
}

export function formatInstant(
  instant: Temporal.Instant,
  displayTimeZone: string,
  options?: Intl.DateTimeFormatOptions,
  separator: string = ' ',
) {
  const zoned = instant.toZonedDateTimeISO(displayTimeZone);
  const formattedTime = formatPlainTime(zoned.toPlainTime(), options);
  const formattedDate = formatPlainDate(zoned.toPlainDate());
  const formattedDateWithoutYear = zoned.toLocaleString('en-US', { month: 'short', day: 'numeric' });

  return {
    formattedTime,
    formattedDate,
    formattedDateWithoutYear,
    formattedDateTime: `${formattedDate}${separator}${formattedTime}`,
    formattedDateWithoutYearAndTime: `${formattedDateWithoutYear}${separator}${formattedTime}`,
  };
}

/**
 * Format a date as "Fri, Feb 20" — short weekday, short month name, and day without zero-padding.
 * Used for teacher-facing table date dividers.
 */
export function formatShortDateWithDayOfWeek(instant: Temporal.Instant, timeZone: string): string {
  const zoned = instant.toZonedDateTimeISO(timeZone);
  const dayOfWeek = zoned.toLocaleString('en-US', { weekday: 'short' });
  const month = zoned.toLocaleString('en-US', { month: 'short' });
  const day = zoned.day;
  return `${dayOfWeek}, ${month} ${day}`;
}

export function formatDateWithDayOfWeek(instant: Temporal.Instant, timeZone: string): string {
  const zoned = instant.toZonedDateTimeISO(timeZone);
  const date = zoned.toPlainDate();
  const dayOfWeek = zoned.toLocaleString('en-US', { weekday: 'short' });
  const month = date.month.toString().padStart(2, '0');
  const day = date.day.toString().padStart(2, '0');
  const year = date.year.toString();
  return `${dayOfWeek}, ${month}/${day}/${year}`;
}

export function formatTimeRange(start: Temporal.Instant, end: Temporal.Instant, timeZone: string): string {
  const startTime = start.toZonedDateTimeISO(timeZone).toPlainTime();
  const endTime = end.toZonedDateTimeISO(timeZone).toPlainTime();
  return `${formatPlainTime(startTime, { hour12: true })} - ${formatPlainTime(endTime, { hour12: true })}`;
}

/**
 * Format session date and time for substitution request display.
 * Returns date as "Feb 11" and time as "6:10 pm - 6:50 pm".
 */
export function formatSubRequestDateTime(
  startInstant: Temporal.Instant,
  endInstant: Temporal.Instant,
  timeZone: string,
): { sessionDateFormatted: string; sessionTimeFormatted: string } {
  const { formattedDateWithoutYear } = formatInstant(startInstant, timeZone);
  return {
    sessionDateFormatted: formattedDateWithoutYear,
    sessionTimeFormatted: formatTimeRange(startInstant, endInstant, timeZone),
  };
}

export function formatDateRange(start: Temporal.Instant, end: Temporal.Instant, timeZone: string): string {
  const formattedStartDate = formatInstant(start, timeZone).formattedDate;
  const formattedEndDate = formatInstant(end, timeZone).formattedDate;
  return `${formattedStartDate}${!isSameDay(start, end, timeZone) ? ` - ${formattedEndDate}` : ''}`;
}

export function lowerCaseAmPm(value: string): string {
  return value.replace(/\b(AM|PM)\b/g, (m) => m.toLowerCase());
}

/**
 * Format session date/time range in the format: "Tue, Apr 24, 6:05 pm - 6:35 pm (US/Pacific)"
 */
export function formatSessionDateTimeRange(
  start: Temporal.Instant,
  end: Temporal.Instant,
  timeZone: string,
  includeTimeZone: boolean = true,
): string {
  const startDateTime = start.toZonedDateTimeISO(timeZone);
  const endDateTime = end.toZonedDateTimeISO(timeZone);

  const startFormatted = lowerCaseAmPm(
    startDateTime.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
  );

  const endFormatted = lowerCaseAmPm(
    endDateTime.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }),
  );

  return `${startFormatted} - ${endFormatted} ${includeTimeZone ? `(${timeZone})` : ''}`;
}

/**
 * Format session date/time range in the format: "12/11, 9:00 AM - 9:30 AM"
 */
export function formatSessionDateTimeRangeNumeric(
  start: Temporal.Instant,
  end: Temporal.Instant,
  timeZone: string,
  showFullDate: boolean = false,
): string {
  const startDateTime = start.toZonedDateTimeISO(timeZone);
  const endDateTime = end.toZonedDateTimeISO(timeZone);

  //Format times
  const startTime = startDateTime
    .toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();

  const endTime = endDateTime
    .toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();

  const startDate = startDateTime.toPlainDate();

  let dateFormatted: string;
  if (showFullDate) {
    const dayOfWeek = startDateTime.toLocaleString('en-US', { weekday: 'short' });
    const month = startDate.month.toString().padStart(2, '0');
    const day = startDate.day.toString().padStart(2, '0');
    const year = startDate.year.toString();
    dateFormatted = `${dayOfWeek}, ${month}/${day}/${year}`;
  } else {
    const day = startDate.day.toString().padStart(2, '0');
    const month = startDate.month.toString().padStart(2, '0');
    dateFormatted = `${month}/${day}`;
  }

  return `${dateFormatted}, ${startTime} - ${endTime}`;
}

/**
 * Format session time range for SMS content, showing times in 12-hour format with am/pm,
 * and only including the am/pm if it differs between start and end times (e.g. "9:00-9:30 am" or "11:30 am-12:15 pm")
 */
export function formatSessionTimeRangeForSMS(
  startInstant: Temporal.Instant,
  endInstant: Temporal.Instant,
  timeZone: string,
): string {
  const startDateTime = startInstant.toZonedDateTimeISO(timeZone);
  const endDateTime = endInstant.toZonedDateTimeISO(timeZone);

  const formatTime = (zonedDateTime: Temporal.ZonedDateTime) => {
    const hh = zonedDateTime.hour % 12 || 12;
    const mm = zonedDateTime.minute.toString().padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const startPeriod = startDateTime.hour < 12 ? 'am' : 'pm';
  const endPeriod = endDateTime.hour < 12 ? 'am' : 'pm';

  if (startPeriod === endPeriod) {
    return `${formatTime(startDateTime)}-${formatTime(endDateTime)} ${endPeriod}`;
  }

  return `${formatTime(startDateTime)} ${startPeriod}-${formatTime(endDateTime)} ${endPeriod}`;
}

/**
 * Get ordinal suffix for a day number (1st, 2nd, 3rd, 4th, etc.)
 */
function getOrdinalSuffix(day: number): string {
  // Special cases: 11th, 12th, 13th
  if (day >= 11 && day <= 13) return 'th';

  // Regular cases based on last digit
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Format an upcoming class date with time, day of week, and ordinal date
 * @param startInstant The start time of the class
 * @param timeZone The timezone to format the date in
 * @returns Formatted string like "2:30 pm, Mon, Jan 15th"
 */
export function formatUpcomingClassDate(startInstant: Temporal.Instant, timeZone: string): string {
  const zoned = startInstant.toZonedDateTimeISO(timeZone);
  const time = zoned
    .toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();

  const dayOfWeek = zoned.toLocaleString('en-US', { weekday: 'short' });
  const month = zoned.toLocaleString('en-US', { month: 'short' });
  const day = zoned.day;

  return `${time}, ${dayOfWeek}, ${month} ${day}${getOrdinalSuffix(day)}`;
}

/**
 * Format meeting blocks into a readable string showing day and time
 * @param meetingBlocks Array of meeting blocks with day and time information
 * @returns Formatted string like "Mon 2:30 pm, Tue 3:00 pm"
 */
export function formatMeetingBlocks(
  meetingBlocks: { day: DayOfWeek; startPlainTime: Temporal.PlainTime; endPlainTime: Temporal.PlainTime }[],
): string {
  if (!meetingBlocks.length) return '';

  return meetingBlocks
    .map((block) => {
      const dayAbbr = DayOfWeekLabelMap[block.day].abbr;
      const time = formatPlainTime(block.startPlainTime, { hour12: true });
      return `${dayAbbr} ${time}`;
    })
    .join(', ');
}

/**
 * Convert a 12-hour format time string to a Temporal.PlainTime object.
 * @param input The time string in 12-hour format (e.g., "2:30 pm", "12:00 am").
 * @returns The corresponding Temporal.PlainTime object, or null if the input is invalid.
 */
export function hoursFormatToPlainTime(input: string): Temporal.PlainTime | null {
  const value = input.trim().toLowerCase();

  const match = value.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);

  if (!match) {
    return null;
  }

  const [, hourRaw, minuteRaw = '00', period] = match;

  let hour = Number(hourRaw);
  const minute = Number(minuteRaw);

  if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 1 || hour > 12 || minute < 0 || minute > 59) {
    return null;
  }

  if (period === 'pm' && hour !== 12) hour += 12;
  if (period === 'am' && hour === 12) hour = 0;

  return new Temporal.PlainTime(hour, minute);
}
